import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import { task, timeout } from 'ember-concurrency';
import type { NamedArgs, PositionalArgs } from 'ember-modifier';
import Modifier from 'ember-modifier';

import styles from './highlight-element.module.css';

const ANIMATION_DURATION_IN_MILLISECONDS = 4000;

interface HighlightElementSignature {
  Args: {
    Named: {
      highlightParentElement?: boolean;
      id: string | undefined;
      isLoading?: boolean;
    };
    Positional: [];
  };
  Element: Element;
}

export default class HighlightElementModifier extends Modifier<HighlightElementSignature> {
  @service declare elementHighlighter: Services['element-highlighter'];
  @service declare router: Services['router'];

  removeHighlightTask = task(
    { drop: true },
    async (element: Element): Promise<void> => {
      if (macroCondition(isTesting())) {
        return;
      }

      // Remove the `highlight` class after animation completes
      await timeout(ANIMATION_DURATION_IN_MILLISECONDS);

      element.classList.remove(styles['highlight']);
    },
  );

  get targetId(): string | undefined {
    const queryParams = this.router.currentRoute?.queryParams;

    return queryParams?.['highlight'] as string | undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  modify(
    element: Element,
    _positional: PositionalArgs<HighlightElementSignature>,
    named: NamedArgs<HighlightElementSignature>,
  ) {
    const { id, highlightParentElement = false, isLoading = false } = named;
    const { targetId } = this;

    if (id === undefined || targetId === undefined) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (id !== targetId) {
      return;
    }

    // Check if this element has already been highlighted to prevent re-highlighting on re-renders
    if (this.elementHighlighter.hasBeenHighlighted(id)) {
      return;
    }

    // Mark this element as highlighted
    this.elementHighlighter.markAsHighlighted(id);

    // Determine which element to highlight
    const targetElement =
      highlightParentElement && element.parentElement
        ? element.parentElement
        : element;

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    targetElement.classList.add(styles['highlight']);

    if (macroCondition(isTesting())) {
      targetElement.classList.add(styles['is-testing']);
    }

    void this.removeHighlightTask.perform(targetElement);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      void this.removeHighlightTask.cancelAll();

      targetElement.classList.remove(styles['highlight']);
      targetElement.classList.remove(styles['is-testing']);
    };
  }
}
